import i18n from "i18next"
import i18nBackend from "i18next-http-backend"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import {
  getEnglishOrdinal,
  getRussianOrdinal,
  getUkrainianOrdinal
} from "./utils/i18n-ordinal"

export const languagePresets = {
  en: ["en", "en-US"],
  ru: ["ru", "by", "kz", "kg", "tj", "uz", "am", "az", "md"],
  uk: ["uk", "uk-UA"]
}

const getFallbackLanguages = (lng: string) => {
  let lang = "en"

  Object.entries(languagePresets).forEach(([key, values]) => {
    if (values.includes(lng)) {
      lang = key
    }
  })

  return lang
}

i18n.on("languageChanged", (lng) => {
  const lang = getFallbackLanguages(lng)

  i18n.language = lang

  document.documentElement.lang = lang
})

i18n
  .use(i18nBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: `/assets/locales/{{lng}}.json?v=${import.meta.env.VITE_APP_VERSION}`
    },
    fallbackLng: (lng) => getFallbackLanguages(lng),
    supportedLngs: ["en", "ru", "uk"],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "i18nextLng",
      caches: ["localStorage"]
    },
    debug: true,
    interpolation: {
      escapeValue: false, // react already safes from xss
      format: (value, format, lng) => {
        if (format === "capitalize")
          return value.charAt(0).toUpperCase() + value.slice(1)

        if (format === "setMineGameAction") {
          if (lng?.includes("en")) return value
          if (lng?.includes("ru"))
            return value === "digging" ? "копаете" : "чините"
          if (lng?.includes("uk"))
            return value === "digging" ? "копаєте" : "ремонтуєте"
        }

        if (format === "setPointsWord") {
          if (lng?.includes("en")) {
            return value + " " + (Math.abs(+value) === 1 ? "point" : "points")
          }
          if (lng?.includes("ru")) {
            let word = "очков"

            const absNumber = Math.abs(+value)
            const lastDigit = absNumber % 10
            const lastTwoDigits = absNumber % 100

            if (lastTwoDigits >= 11 && lastTwoDigits <= 14) word = "очков"
            if (lastDigit === 1) word = "очко"
            if (lastDigit >= 2 && lastDigit <= 4) word = "очка"
            return value + " " + word
          }
          if (lng?.includes("uk")) {
            let word = "очок"

            const absNumber = Math.abs(+value)
            const lastDigit = absNumber % 10
            const lastTwoDigits = absNumber % 100

            if (lastTwoDigits >= 11 && lastTwoDigits <= 14) word = "очок"
            if (lastDigit === 1) word = "очко"
            if (lastDigit >= 2 && lastDigit <= 4) word = "очка"
            return value + " " + word
          }
        }

        if (format === "formatItemType") {
          if (lng?.includes("en")) {
            if (value === "apparel") return "skin"
            return value
          }
          if (lng?.includes("ru")) {
            if (value === "shovel") return "лопату"
            if (value === "elixir") return "эликсир"
            if (value === "preform") return "заготовку"
            if (value === "runes") return "руну"
            if (value === "apparel") return "скин"
          }
          if (lng?.includes("uk")) {
            if (value === "shovel") return "лопату"
            if (value === "elixir") return "еліксир"
            if (value === "preform") return "заготовку"
            if (value === "runes") return "руну"
            if (value === "apparel") return "скін"
          }
        }

        if (format === "ordinal") {
          if (lng === "en") return getEnglishOrdinal(value as number)
          if (lng === "uk") return getUkrainianOrdinal(value as number)
          if (lng === "ru") return getRussianOrdinal(value as number)
        }

        if (format === "lowercase") {
          return value?.toLowerCase()
        }

        return value
      }
    }
  })

export default i18n
